import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API } from '@/lib/apiBase';
import { getApplicationId } from '@/lib/applicationId';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

function sessionKey(versionId) {
  return `prysme_analytics_version_popup_closed:${versionId}`;
}

/**
 * Popup nouveautés — uniquement au lancement de Prysm'Analytics (utilisateur connecté).
 */
export default function VersionWhatsNewModal() {
  const { user, token } = useAuth();
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [hideForever, setHideForever] = useState(false);

  useEffect(() => {
    if (!user?.id || !token) {
      setOpen(false);
      setItem(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/app-versions/pending`, {
          params: { application_id: getApplicationId() },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.data?.show || !res.data?.item) return;
        const versionId = res.data.item.id;
        try {
          if (sessionStorage.getItem(sessionKey(versionId))) return;
        } catch {
          /* ignore */
        }
        setItem(res.data.item);
        setHideForever(false);
      } catch {
        if (!cancelled) setItem(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, token]);

  useEffect(() => {
    setOpen(Boolean(user && item));
  }, [user, item]);

  const close = async () => {
    if (item?.id) {
      try {
        sessionStorage.setItem(sessionKey(item.id), '1');
      } catch {
        /* ignore */
      }
      if (hideForever && token) {
        try {
          await axios.post(
            `${API}/app-versions/${item.id}/dismiss`,
            { hide_forever: true },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch {
          /* ignore */
        }
      }
    }
    setOpen(false);
    setItem(null);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Nouveautés Prysm&apos;Analytics
            {item.version ? ` — v${item.version}` : ''}
          </DialogTitle>
          <DialogDescription>
            Corrections et améliorations de cette version.
          </DialogDescription>
        </DialogHeader>
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
          {item.changelog || 'Aucun détail.'}
        </pre>
        <div className="flex items-center gap-2">
          <Checkbox
            id="hide-version-popup-analytics"
            checked={hideForever}
            onCheckedChange={(v) => setHideForever(!!v)}
          />
          <Label htmlFor="hide-version-popup-analytics" className="text-sm font-normal">
            Ne plus afficher cette version
          </Label>
        </div>
        <DialogFooter>
          <Button type="button" onClick={close}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
